import cn from 'classnames'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { Button } from 'core-components/library/button'
import { Loader } from 'core-components/library/loader'
import { IModalContent } from 'core-components/library/modalDialog'
import { Tooltip } from 'core-components/library/tooltip'
import { Tag, TQualityBand } from 'partner360/components/qualityIndicator'

import { PLAN_REVIEW_PLANNING_MEASURE_ENABLED } from '../config/features'
import { getPlanReviewItem, PLAN_REVIEW_GROUPS } from '../config/steps'
import { PLAN_REVIEW_TRANSLATIONS } from '../config/translations'
import { getFirstSectionToReview } from '../lib/getFirstSectionToReview'
import { getRequiredJustificationSections } from '../lib/getRequiredJustificationSections'
import { isPlanReviewJustificationValid } from '../lib/isPlanReviewJustificationValid'
import {
  IPlanReviewJustificationTarget,
  PlanReviewSection,
  TPlanReviewJustifications,
  TPlanReviewOffering,
  TPlanReviewQualitySection,
  TPlanReviewRole,
} from '../model/types'
import { usePlanReviewQuality } from '../model/usePlanReviewQuality'

import { FinancialQualityStep } from './FinancialQualityStep'
import { InitiativesQualityStep } from './InitiativesQualityStep'
import { JustificationBanner } from './justification/JustificationBanner'
import { JustificationDialog } from './justification/JustificationDialog'
import { PlanningMeasureStep } from './PlanningMeasureStep'
import { PlanReviewSummaryStep } from './PlanReviewSummaryStep'
import { PlanReviewText } from './PlanReviewText'

import styles from './PlanReviewDialog.module.scss'

interface IPlanReviewDialogContentProps extends IModalContent {
  partnerId: string
  fYear: number
  programType: TPlanReviewOffering
  isGlobal: boolean
  role: TPlanReviewRole
  continueCaption: string
  section: PlanReviewSection
  justifications: TPlanReviewJustifications
  isJustificationsLoading: boolean
  isJustificationSaving: boolean
  justificationsLoadError: string | null
  justificationSaveError: string | null
  onSectionChange: (section: PlanReviewSection) => void
  onReloadJustifications: () => Promise<boolean>
  onSaveJustification: (target: IPlanReviewJustificationTarget, value: string) => Promise<boolean>
  onContinue: () => void
  onReject: () => void
  showActions?: boolean
  showJustifications?: boolean
  showSummary?: boolean
}

interface IQualityScore {
  score: number | null
  band: TQualityBand | null
}

const isFinancialSection = (
  section: PlanReviewSection
): section is PlanReviewSection.FinancialGrowth | PlanReviewSection.SobGrowth =>
  section === PlanReviewSection.FinancialGrowth || section === PlanReviewSection.SobGrowth

const QualityBadge = ({ score, band }: IQualityScore) => (
  <Tag variant={band ?? undefined} translation={score === null ? '-' : `${score}%`} />
)

export const PlanReviewDialogContent = ({
  partnerId,
  fYear,
  programType,
  isGlobal,
  role,
  continueCaption,
  section,
  justifications,
  isJustificationsLoading,
  isJustificationSaving,
  justificationsLoadError,
  justificationSaveError,
  onSectionChange,
  onReloadJustifications,
  onSaveJustification,
  onContinue,
  onReject,
  showActions = true,
  showJustifications = true,
  showSummary = false,
  closeModal,
}: IPlanReviewDialogContentProps) => {
  const t = useSafetyTranslation()
  // This check belongs to acknowledgment, not to the existing quality-only views.
  const isPlanningMeasureEnabled = PLAN_REVIEW_PLANNING_MEASURE_ENABLED && showJustifications
  const [justificationTarget, setJustificationTarget] =
    useState<IPlanReviewJustificationTarget | null>(null)
  const {
    financialQuality,
    hasFinancialPlan,
    initiativesQuality,
    planningMeasureQuality,
    isPlanningMeasureError,
    isLoading: isQualityLoading,
  } = usePlanReviewQuality({
    partnerId,
    fYear,
    programType,
    isGlobal,
    refreshPlanningOnOpen: isPlanningMeasureEnabled,
  })
  const isLoading = isQualityLoading || (showJustifications && isJustificationsLoading)
  const hasPlanningMeasureError = isPlanningMeasureEnabled && Boolean(isPlanningMeasureError)
  const canEditJustification = showJustifications && role === 'hp'
  // Set once a section is chosen in this opening, by the user or by the review start below.
  const isSectionChosenRef = useRef(false)

  const getGroupScore = (groupKey: (typeof PLAN_REVIEW_GROUPS)[number]['key']): IQualityScore => {
    if (isLoading) {
      return { score: null, band: null }
    }

    return groupKey === 'financial' ? financialQuality.overall : initiativesQuality.overall
  }

  const getSectionScore = useCallback(
    (itemSection: PlanReviewSection): IQualityScore => {
      if (isLoading) {
        return { score: null, band: null }
      }

      switch (itemSection) {
        case PlanReviewSection.Summary:
          return { score: null, band: null }
        case PlanReviewSection.FinancialGrowth:
          return financialQuality.financialGrowth
        case PlanReviewSection.SobGrowth:
          return financialQuality.sobGrowth
        case PlanReviewSection.PlanningMeasure:
          return isPlanningMeasureEnabled && planningMeasureQuality
            ? planningMeasureQuality
            : { score: null, band: null }
        case PlanReviewSection.FinancialPlanCoverage:
          return initiativesQuality.financialPlanCoverage
        case PlanReviewSection.DescriptionQuality:
          return initiativesQuality.descriptionQuality
        case PlanReviewSection.StrategicGrowthAreas:
          return initiativesQuality.strategicGrowthAreas
      }
    },
    [
      financialQuality,
      initiativesQuality,
      isLoading,
      isPlanningMeasureEnabled,
      planningMeasureQuality,
    ]
  )

  const requiredJustificationSectionList = useMemo(
    () =>
      getRequiredJustificationSections({
        financialQuality,
        hasFinancialPlan,
        initiativesQuality,
        planningMeasureQuality,
        isPlanningMeasureEnabled,
      }),
    [
      financialQuality,
      hasFinancialPlan,
      initiativesQuality,
      planningMeasureQuality,
      isPlanningMeasureEnabled,
    ]
  )
  const requiredJustificationSections = useMemo(
    () => new Set(requiredJustificationSectionList),
    [requiredJustificationSectionList]
  )
  const hasOverallJustification = isPlanReviewJustificationValid(
    justifications[PlanReviewSection.Summary]
  )
  // Justifications are entered by the PBM only, so only the PBM is held back by missing ones.
  const sectionsMissingJustification = useMemo(
    () =>
      new Set(
        isLoading || !canEditJustification || hasOverallJustification
          ? []
          : requiredJustificationSectionList.filter(
              (itemSection) => !isPlanReviewJustificationValid(justifications[itemSection])
            )
      ),
    [
      canEditJustification,
      hasOverallJustification,
      isLoading,
      justifications,
      requiredJustificationSectionList,
    ]
  )

  const selectSection = (itemSection: PlanReviewSection) => {
    isSectionChosenRef.current = true
    onSectionChange(itemSection)
  }

  // Start the review at the topmost item below High once the quality is known.
  useEffect(() => {
    if (showSummary || isLoading || isSectionChosenRef.current) {
      return
    }

    isSectionChosenRef.current = true
    const firstSectionToReview = getFirstSectionToReview(
      (itemSection) => getSectionScore(itemSection).band,
      isPlanningMeasureEnabled
    )

    if (firstSectionToReview) {
      onSectionChange(firstSectionToReview)
    }
  }, [getSectionScore, isLoading, isPlanningMeasureEnabled, onSectionChange, showSummary])

  const saveJustificationAndAdvance = async (
    target: IPlanReviewJustificationTarget,
    value: string
  ) => {
    const wasSaved = await onSaveJustification(target, value)

    if (!wasSaved || target.key === PlanReviewSection.Summary) {
      return wasSaved
    }

    const currentIndex = requiredJustificationSectionList.indexOf(
      target.key as TPlanReviewQualitySection
    )
    const nextSection = requiredJustificationSectionList[currentIndex + 1]

    if (nextSection) {
      selectSection(nextSection)
    } else if (showSummary) {
      selectSection(PlanReviewSection.Summary)
    }

    return true
  }

  return (
    <div className={styles.container}>
      <div className={styles.main}>
        <nav className={styles.sidebar} aria-label="Plan quality review sections">
          {showSummary && (
            <button
              type="button"
              role="tab"
              className={cn(styles.tab, styles.summaryTab, {
                [styles.tabActive]: section === PlanReviewSection.Summary,
              })}
              onClick={() => selectSection(PlanReviewSection.Summary)}
              aria-selected={section === PlanReviewSection.Summary}
            >
              <span className={styles.tabLabel}>
                <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.menu} />
              </span>
            </button>
          )}

          {PLAN_REVIEW_GROUPS.map((group) => {
            const groupScore = getGroupScore(group.key)

            return (
              <Fragment key={group.key}>
                {group.key === 'initiatives' && isPlanningMeasureEnabled && (
                  <button
                    type="button"
                    role="tab"
                    className={cn(styles.tab, styles.summaryTab, {
                      [styles.tabActive]: section === PlanReviewSection.PlanningMeasure,
                      [styles.tabMissingJustification]: sectionsMissingJustification.has(
                        PlanReviewSection.PlanningMeasure
                      ),
                    })}
                    onClick={() => selectSection(PlanReviewSection.PlanningMeasure)}
                    aria-selected={section === PlanReviewSection.PlanningMeasure}
                  >
                    <Tooltip
                      className={styles.labelTooltip}
                      tipContent={() => (
                        <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.planningMeasure.title} />
                      )}
                    >
                      <span className={cn(styles.tabLabel, styles.planningMeasureLabel)}>
                        <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.planningMeasure.title} />
                      </span>
                    </Tooltip>
                    <QualityBadge {...getSectionScore(PlanReviewSection.PlanningMeasure)} />
                  </button>
                )}
                <div className={styles.menuGroup}>
                  <div className={styles.groupHeader}>
                    <Tooltip
                      className={styles.labelTooltip}
                      tipContent={() => <Translation text={group.title} />}
                    >
                      <span className={styles.groupLabel}>
                        <Translation text={group.title} span />
                      </span>
                    </Tooltip>
                    <QualityBadge {...groupScore} />
                  </div>

                  <div className={styles.groupItems} role="tablist">
                    {group.items.map((item) => {
                      const itemScore = getSectionScore(item.key)
                      const isActive = item.key === section

                      return (
                        <button
                          key={item.key}
                          type="button"
                          role="tab"
                          className={cn(styles.tab, {
                            [styles.tabActive]: isActive,
                            [styles.tabMissingJustification]: sectionsMissingJustification.has(
                              item.key
                            ),
                          })}
                          onClick={() => selectSection(item.key)}
                          aria-selected={isActive}
                        >
                          <Tooltip
                            className={styles.labelTooltip}
                            tipContent={() => <Translation text={item.title} />}
                          >
                            <span className={styles.tabLabel}>
                              <Translation text={item.title} span />
                            </span>
                          </Tooltip>
                          <QualityBadge {...itemScore} />
                        </button>
                      )
                    })}
                  </div>
                </div>
              </Fragment>
            )
          })}
        </nav>

        <div className={styles.panel} role="tabpanel">
          {isLoading ? (
            <div className={styles.loader}>
              <Loader />
            </div>
          ) : (
            <>
              {showJustifications && justificationsLoadError && (
                <div className={styles.justificationsLoadError} role="alert">
                  <span>{justificationsLoadError}</span>
                  <Button
                    size="sm"
                    variant="outlined"
                    onClick={() => void onReloadJustifications()}
                  >
                    Retry
                  </Button>
                </div>
              )}

              {hasPlanningMeasureError && (
                <div
                  className={cn(styles.justificationsLoadError, styles.planningMeasureLoadError)}
                  role="alert"
                >
                  <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.planningMeasure.loadError} />
                </div>
              )}

              {section === PlanReviewSection.Summary ? (
                <PlanReviewSummaryStep
                  role={role}
                  financialQuality={financialQuality}
                  initiativesQuality={initiativesQuality}
                  planningMeasureQuality={
                    isPlanningMeasureEnabled ? planningMeasureQuality : undefined
                  }
                  isReviewBlocked={hasPlanningMeasureError}
                  weakSections={requiredJustificationSectionList}
                  justifications={justifications}
                  isSaving={isJustificationSaving}
                  saveError={justificationSaveError}
                  onSectionChange={selectSection}
                  onSaveOverall={(value) =>
                    onSaveJustification(
                      {
                        key: PlanReviewSection.Summary,
                        label: PLAN_REVIEW_TRANSLATIONS.summary.overallJustification.key,
                      },
                      value
                    )
                  }
                  onAcknowledge={onContinue}
                />
              ) : section === PlanReviewSection.PlanningMeasure ? (
                isPlanningMeasureEnabled &&
                !hasPlanningMeasureError &&
                planningMeasureQuality && (
                  <PlanningMeasureStep
                    quality={planningMeasureQuality}
                    fYear={fYear}
                    justification={justifications[PlanReviewSection.PlanningMeasure]}
                    canEdit={canEditJustification}
                    onJustify={() =>
                      setJustificationTarget({
                        key: PlanReviewSection.PlanningMeasure,
                        label: t(PLAN_REVIEW_TRANSLATIONS.planningMeasure.title.key, {
                          defaultValue: PLAN_REVIEW_TRANSLATIONS.planningMeasure.title.fallback,
                        }),
                      })
                    }
                  />
                )
              ) : (
                <>
                  {showJustifications && requiredJustificationSections.has(section) && (
                    <JustificationBanner
                      justification={justifications[section]}
                      canEdit={canEditJustification}
                      onClick={() =>
                        setJustificationTarget({
                          key: section,
                          label: getPlanReviewItem(section)?.title ?? section,
                        })
                      }
                    />
                  )}

                  {isFinancialSection(section) ? (
                    <FinancialQualityStep
                      key={section}
                      section={section}
                      quality={financialQuality}
                      hasPlan={hasFinancialPlan}
                    />
                  ) : (
                    <InitiativesQualityStep
                      key={section}
                      section={section}
                      quality={initiativesQuality}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {showActions && (
        <div className={styles.footer}>
          {canEditJustification ? (
            <>
              <Button color="grey" variant="outlined" onClick={closeModal}>
                <Translation text="partnercommitment-cancel" span actionsInside />
              </Button>
              <Button
                disabled={
                  isLoading ||
                  Boolean(justificationsLoadError) ||
                  hasPlanningMeasureError ||
                  sectionsMissingJustification.size > 0
                }
                onClick={onContinue}
              >
                <Translation text={continueCaption} span actionsInside />
              </Button>
            </>
          ) : (
            <>
              <Button disabled={isLoading || hasPlanningMeasureError} onClick={onContinue}>
                Acknowledge
              </Button>
              <Button color="danger" disabled={isLoading} onClick={onReject}>
                Reject
              </Button>
            </>
          )}
        </div>
      )}

      {showJustifications && (
        <JustificationDialog
          target={justificationTarget}
          value={justificationTarget ? justifications[justificationTarget.key] : ''}
          isSaving={isJustificationSaving}
          saveError={justificationSaveError}
          onClose={() => {
            if (!isJustificationSaving) {
              setJustificationTarget(null)
            }
          }}
          onSave={(value) =>
            justificationTarget
              ? saveJustificationAndAdvance(justificationTarget, value)
              : Promise.resolve(false)
          }
        />
      )}
    </div>
  )
}
